import { LOCAL_STORAGE, LocalStorage } from '@blink/components/src/utils';
import { formatUnits, parseUnits } from 'ethers';

import { UserBorrowItem } from '@/features/Pools/ui/UserBorrowSection/ui';
import { Agreement, Token, UserBorrow } from '@/shared/api/borrow';
import { Vault } from '@/shared/api/vault';

const sanitizeInput = (value: string, decimals: number): string => {
    let clean = toDecimalString(value || '0', decimals)
        .replace(/,/g, '')
        .trim();

    if (clean === '.' || clean === '') {
        return '0';
    }

    if (clean.startsWith('.')) {
        clean = '0' + clean;
    }

    if (clean.endsWith('.')) {
        clean = clean.slice(0, -1);
    }

    return trimDecimals(clean, decimals);
};

export const getTransformedUserBorrow = (object: UserBorrow): UserBorrowItem => {
    return {
        address: object.address,
        id: object.leverage.id,
        apy: object.apy || '0',
        leverageAddress: object.leverage.address,
        name: object.leverage.name,
        symbol: object.leverage.symbol,
        decimals: object.leverage.decimals,
        amount: object.leverage.amount,
        openedAt: object.openedAt,
        maintenance: object.maintenance,
        history: object.history,
        agreementAddress: object.agreementAddress,
    };
};

export const getTransformedUserBorrowList = (objectsArray: UserBorrow[]): UserBorrowItem[] => {
    return objectsArray
        .sort((a, b) => {
            return new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime();
        })
        .map((object: UserBorrow) => getTransformedUserBorrow(object));
};

export const mapAgreementData = (arr1: Agreement[], arr2: Token[]) => {
    return arr1.map((item) => {
        const agreementMatches = arr2.filter((subItem) => subItem.id === item.leverageTokenId);
        return {
            ...item,
            leverage: agreementMatches[0],
        };
    });
};

export const mapVaultData = (arr1: Vault[], arr2: Token[]) => {
    return arr1.map((item) => {
        const vaultMatches = arr2.filter((subItem) => subItem.id === item.leverageTokenId);

        return {
            ...item,
            leverage: vaultMatches[0],
        };
    });
};

export const compareBigNumbers = (numStr1: string, numStr2: string, decimals: number) => {
    const bigNumber1 = parseUnits(numStr1, decimals);
    const bigNumber2 = parseUnits(numStr2, decimals);

    if (bigNumber1 > bigNumber2) {
        return 1;
    } else if (bigNumber1 < bigNumber2) {
        return -1;
    } else {
        return 0;
    }
};

export const divideFixedDecimals = (numStr1: string, numStr2: string, decimals: number) => {
    const [num1Amount, num1Decimals] = numStr1?.split('.') || [];
    const [num2Amount, num2Decimals] = numStr2?.split('.') || [];

    const num1 = num1Decimals ? `${num1Amount}.${num1Decimals.substring(0, decimals)}` : num1Amount;
    const num2 = num2Decimals ? `${num2Amount}.${num2Decimals.substring(0, decimals)}` : num2Amount;

    const bigNumber1 = parseUnits(num1, decimals);
    const bigNumber2 = parseUnits(num2, decimals);

    const result = bigNumber1 / bigNumber2;
    return formatUnits(result, decimals);
};

export const multiplyFixedDecimals = (numStr1: string, numStr2: string, decimals: number) => {
    const [num1Amount, num1Decimals] = numStr1?.split('.') || [];
    const [num2Amount, num2Decimals] = numStr2?.split('.') || [];

    const num1 = num1Decimals ? `${num1Amount}.${num1Decimals.substring(0, decimals)}` : num1Amount;
    const num2 = num2Decimals ? `${num2Amount}.${num2Decimals.substring(0, decimals)}` : num2Amount;

    const bigNumber1 = parseUnits(num1, decimals);
    const bigNumber2 = parseUnits(num2, decimals);

    const result = bigNumber1 * bigNumber2;
    return formatUnits(result, decimals);
};

export const subtractFixedDecimals = (numStr1: string, numStr2: string, decimals: number) => {
    const safeNum1 = sanitizeInput(numStr1, decimals);
    const safeNum2 = sanitizeInput(numStr2, decimals);

    const bigNumber1 = parseUnits(safeNum1, decimals);
    const bigNumber2 = parseUnits(safeNum2, decimals);

    const result = bigNumber1 - bigNumber2;

    return formatUnits(result, decimals);
};

export const sumFixedDecimals = (numStr1: string, numStr2: string, decimals: number) => {
    const safeNum1 = sanitizeInput(numStr1, decimals);
    const safeNum2 = sanitizeInput(numStr2, decimals);

    const bigNumber1 = parseUnits(safeNum1, decimals);
    const bigNumber2 = parseUnits(safeNum2, decimals);

    const result = bigNumber1 + bigNumber2;
    return formatUnits(result, decimals);
};

export const toDecimalString = (numStr: string, decimals = 18) => {
    if (numStr?.toLowerCase().includes('e')) {
        return Number(numStr)
            .toFixed(decimals)
            .replace(/\.?0+$/, '');
    }

    return numStr;
};

export const formatWithMaxPrecision = (
    n: string,
    decimals: number = 18,
    maxPrecision: number = 5,
): string => {
    if (!n || isNaN(Number(n))) {
        throw new Error('Invalid input: The input must be a non-empty valid number string.');
    }

    if (n === '≈0') return '≈0';

    const value = parseUnits(n, decimals);
    const formattedValue = formatUnits(value, decimals);

    const [integerPart, fractionalPart] = formattedValue.split('.');

    const extra = integerPart.length - 1;
    maxPrecision -= maxPrecision > extra ? extra % maxPrecision : maxPrecision;

    const trimmedFractionalPart = fractionalPart.slice(0, maxPrecision).replace(/0+$/, '');

    if (trimmedFractionalPart === '') {
        // The number is less than max precision but not zero.
        if (integerPart === '0' && fractionalPart !== '0') {
            return '≈0';
        }
        return integerPart;
    }

    return `${integerPart}.${trimmedFractionalPart}`;
};

export const cleanLocalStorageWalletConnect = () => {
    LocalStorage.setItem(LOCAL_STORAGE.WALLET, '');
    LocalStorage.setItem(LOCAL_STORAGE.WALLET_CONNECT_ADDRESS, '');
    LocalStorage.setItem(LOCAL_STORAGE.WALLET_CONNECT_MARGIN_ADDRESS, '');
    LocalStorage.setItem(LOCAL_STORAGE.WALLET_CONNECT_URI, '');
    LocalStorage.setItem(LOCAL_STORAGE.WALLET_CONNECT_TOPIC, '');
    LocalStorage.setItem(LOCAL_STORAGE.CONNECTED_DAPP, '');
};

export const isValidBigIntValue = (value: string, limit: string) => {
    const normalizedValue = value.replace(',', '.');

    if (!/^\d+(\.\d+)?$/.test(normalizedValue)) {
        return false;
    }

    const floatValue = parseFloat(normalizedValue);
    if (isNaN(floatValue)) {
        return false;
    }

    try {
        const [integerPart, fractionalPart = ''] = normalizedValue.split('.');
        if (fractionalPart === '' && normalizedValue.includes('.')) {
            return false;
        }

        const numericValue = BigInt(integerPart + fractionalPart);
        const scale = 10n ** BigInt(fractionalPart.length);
        const maxValue = BigInt(limit) * scale;
        return numericValue > 0n && numericValue <= maxValue;
    } catch (error) {
        return false;
    }
};

export const isGreaterOrEqualThan = (num1: string, num2: string, decimals = 18) => {
    const cleanedNum1 = sanitizeInput(toDecimalString(num1, decimals), decimals);
    const cleanedNum2 = sanitizeInput(toDecimalString(num2, decimals), decimals);

    const bigNumber1 = parseUnits(cleanedNum1, decimals);
    const bigNumber2 = parseUnits(cleanedNum2, decimals);

    return bigNumber1 >= bigNumber2;
};

export const trimDecimals = (value: string, decimals: number) => {
    const [integerPart, fractionalPart = ''] = value.split('.');
    const trimmedFractionalPart = fractionalPart.slice(0, decimals);
    return fractionalPart ? `${integerPart}.${trimmedFractionalPart}` : integerPart;
};

export const formatWithCommas = (value: number | string | undefined): string => {
    if (value === undefined) return 'N/A';

    if (value === '≈0') return '≈0';

    let stringValue = value.toString();

    if (stringValue.includes('~')) {
        stringValue = stringValue.replace('~', '');
    }

    const hasDecimal = stringValue.includes('.');
    const [integerPart, decimalPart = ''] = stringValue.split('.');
    const formattedInteger = Number(integerPart).toLocaleString('en-US');

    if (hasDecimal) {
        return `${formattedInteger}.${decimalPart}`;
    }

    return formattedInteger;
};

export const trimTrailingZeros = (amount: string, decimals: number = 18): string => {
    const num = Number(amount);

    if (Number.isInteger(num)) {
        return num.toString();
    }

    const result = num
        .toString()
        .replace(/(\.\d*?[1-9])0+$/g, '$1')
        .replace(/\.0+$/, '');

    const [integerPart, decimalPart = ''] = result.split('.');

    if (decimalPart && decimalPart.length > decimals) {
        return `${integerPart}.${decimalPart.slice(0, decimals)}`;
    }

    return result;
};

export const formatSmart = (raw: string | number): string => {
    if (!raw) return 'N/A';

    if (raw === '≈0') return '≈0';

    const value = Number(raw);

    if (value === 0) return '0';
    if (value < 0.00001) return '≈0';

    const fixed = value.toFixed(5);

    const trimmed = fixed.replace(/\.?0+$/, '');

    return formatWithCommas(trimmed);
};

export const formatPercentage = (value: number | string) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(Number(value) * 100);
};
